import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { usePageContent } from "../../hooks/usePageContent";
import "./index.module.css";

// outcome: 'unsubmitted' | 'pending' | 'success' | 'failed'

const HeroComboChecker = ({
  onCheckCombo,
  selectStylePath = "/create/select-a-style",
  maxChars = { car: 6, motorcycle: 5 },
  placeholder = { car: "CUSTOM", motorcycle: "MOTO5" },
  disclaimerText = "Representation of plates provided are for illustrative purposes only",
  checkAvailButton = "Check availability",
  selectStyleLabel = "Select a style",
  tryAnotherComboText = "Try another combo",
  resetLabel = "Reset combo",
  recommenderTitle = "Similar combinations available",
  recommenderLoadingPrimary = "Curating unique alternatives",
  recommenderLoadingSecondary = "Personalised for you",
  recommendations = [],
  recommendationsLoading = false,
  serverError = null,
}) => {
  const { find } = usePageContent("hero-combo-checker");
  const c = find("heroComboChecker") || {};

  const [vehicleType, setVehicleType] = useState("car");
  const [combo, setCombo] = useState("");
  const [outcome, setOutcome] = useState("unsubmitted");
  const formRef = useRef(null);

  const currentMaxChars =
    vehicleType === "car" ? maxChars.car : maxChars.motorcycle;
  const currentPlaceholder =
    vehicleType === "car" ? placeholder.car : placeholder.motorcycle;
  const trimmedCombo = combo.trim().toUpperCase();
  const comboIsWithValidFormat =
    trimmedCombo.length >= 2 && /^[A-Z0-9]+$/.test(trimmedCombo);
  const comboIsValid =
    comboIsWithValidFormat && trimmedCombo.length <= currentMaxChars;

  const instructionsText = (c.instructionsTemplate || 'Enter 2 to {max} letters/numbers').replace('{max}', String(currentMaxChars));

  // After submitting, drop the vehicle toggle and visually hide the label/instructions so they leave no gap (matches prod)
  const isUnsubmitted = outcome === "unsubmitted";

  const handleVehicleTypeChange = (type) => {
    setVehicleType(type);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comboIsValid || outcome === "pending") return;

    setOutcome("pending");
    try {
      const result = await onCheckCombo(trimmedCombo, vehicleType);
      setOutcome(result ? "success" : "failed");
    } catch {
      setOutcome("failed");
    }
  };

  const resetForm = () => {
    setCombo("");
    setOutcome("unsubmitted");
  };

  const styleSelectorHref = `${selectStylePath}?vehicleType=${vehicleType}&combination=${trimmedCombo}`;

  return (
    <section
      id="hero-check-combo"
      aria-labelledby="home-combo-title"
      className="home-combo"
      tabIndex="-1"
    >
      <h2 id="home-combo-title" className="visuallyhidden">
        {c.sectionTitle || 'Plate combination checker'}
      </h2>

      {/* ARIA live regions */}
      <div role="alert" className="visuallyhidden">
        {outcome === "pending" && (
          <span>{c.checkingText || 'Checking availability. Please wait.'}</span>
        )}
      </div>

      <div
        role="alert"
        tabIndex="-1"
        className={
          outcome === "failed" ? "home-combo__failure-message" : undefined
        }
      >
        {outcome === "failed" && (
          <>
            <h3 className="home-combo__failure-message-title">
              {c.failedHeading || 'Unavailable combo'}
            </h3>
            <p className="home-combo__failure-message-description">
              {c.failedDescription || "Either someone beat you to it or it's on our \"naughty list\"."}
            </p>
            <p className="visuallyhidden">
              Click the button below to try again. Alternatively, similar
              combinations might be available. If so, they will be listed
              underneath the form shortly.
            </p>
          </>
        )}
      </div>

      <div
        role="alert"
        className={
          outcome === "success" ? "home-combo__available-message" : undefined
        }
      >
        {outcome === "success" && (
          <>
            <h3 className="home-combo__available-message-title">
              {c.availableHeading || 'Your combo is available!'}
            </h3>
            <p className="visuallyhidden">
              Select a style to continue building your plate.
            </p>
          </>
        )}
      </div>

      {serverError && (
        <div role="alert" tabIndex="-1">
          <div className="error-bar">
            <p className="error-bar__message">{serverError}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form
        id="quick-check-form"
        autoComplete="off"
        ref={formRef}
        onSubmit={handleSubmit}
      >
        {(outcome === "unsubmitted" || outcome === "pending") && (
          <h3 className="home-combo__title">{c.formTitle || 'Create your plate'}</h3>
        )}
        {isUnsubmitted && (
          <fieldset className="home-combo__type">
            <legend className="visuallyhidden">{c.vehicleLegend || 'Vehicle type'}</legend>
            <div>
              <input
                className="visuallyhidden visuallyhidden--no-focus"
                type="radio"
                name="home-combo-type"
                id="home-combo-type--car"
                value="car"
                checked={vehicleType === "car"}
                onChange={() => handleVehicleTypeChange("car")}
              />
              <label
                className="home-combo__type-label"
                htmlFor="home-combo-type--car"
              >
                {c.carLabel || 'Car'}
              </label>
            </div>
            <div>
              <input
                className="visuallyhidden visuallyhidden--no-focus"
                type="radio"
                name="home-combo-type"
                id="home-combo-type--motorbike"
                value="motorcycle"
                checked={vehicleType === "motorcycle"}
                onChange={() => handleVehicleTypeChange("motorcycle")}
              />
              <label
                className="home-combo__type-label"
                htmlFor="home-combo-type--motorbike"
              >
                {c.motorbikeLabel || 'Motorbike'}
              </label>
            </div>
          </fieldset>
        )}

        <div
          className={`home-combo__combo ${
            outcome !== "unsubmitted" ? "home-combo__combo--read-only" : ""
          }`}
        >
          <label
            className={`home-combo__combo-label${isUnsubmitted ? "" : " visuallyhidden"}`}
            htmlFor="home-combo-combo"
          >
            {c.inputLabel || 'Enter a combination'}
            <span className="visuallyhidden"> {c.inputLabelRequired || '(required)'}</span>
          </label>
          <div
            className={`home-combo__combo-input-container ${
              outcome === "failed"
                ? "home-combo__combo-input-container--failed"
                : ""
            } ${
              outcome === "success"
                ? "home-combo__combo-input-container--success"
                : ""
            }`}
          >
            <input
              id="home-combo-combo"
              placeholder={currentPlaceholder}
              value={combo}
              onChange={(e) =>
                setCombo(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))
              }
              aria-describedby="home-combo-combo-description"
              maxLength={currentMaxChars}
              required
              readOnly={outcome !== "unsubmitted"}
              className={`home-combo__combo-input ${
                outcome !== "unsubmitted"
                  ? "home-combo__combo-input--readonly"
                  : ""
              }`}
            />
          </div>
          <div
            className={`home-combo__combo-instructions${isUnsubmitted ? "" : " visuallyhidden"}`}
          >
            <p
              className="home-combo__combo-description"
              id="home-combo-combo-description"
            >
              {instructionsText}
            </p>
            <div className="home-combo__combo-count" aria-hidden="true">
              {trimmedCombo.length}/{currentMaxChars}
            </div>
          </div>
          {!isUnsubmitted && (
            <p className="home-combo__combo-image-disclaimer">
              <small className="home-combo__combo-image-disclaimer-inner">
                {disclaimerText}
              </small>
            </p>
          )}
        </div>

        <div className="home-combo__buttons">
          {((outcome === "unsubmitted" && comboIsValid) ||
            outcome === "pending") && (
            <button
              type="submit"
              className={`home-combo__button ${
                outcome === "pending" ? "home-combo__button--loading" : ""
              }`}
              disabled={outcome === "pending"}
            >
              <span className={outcome === "pending" ? "visuallyhidden" : ""}>
                {checkAvailButton}
                {outcome === "pending" && (
                  <span className="visuallyhidden"> (currently checking)</span>
                )}
              </span>
            </button>
          )}
          {outcome === "success" && (
            <Link className="home-combo__button" to={styleSelectorHref}>
              {selectStyleLabel}
            </Link>
          )}
          {outcome === "failed" && (
            <button
              className="home-combo__button home-combo__button--reset"
              type="button"
              onClick={resetForm}
            >
              {tryAnotherComboText}
            </button>
          )}
          {(outcome === "success" ||
            (outcome === "unsubmitted" && combo.length > 0)) && (
            <button
              className="home-combo__button home-combo__button--reset-lite"
              type="button"
              onClick={resetForm}
            >
              {resetLabel}
            </button>
          )}
        </div>
      </form>

      {/* Recommendations (shown when failed) */}
      {outcome === "failed" && (
        <div className="home-combo__recommendations">
          {recommendationsLoading && (
            <div className="home-combo__recommendations-loading">
              <h3 className="home-combo__recommendations-loading-primary">
                {recommenderLoadingPrimary}
              </h3>
              <p className="home-combo__recommendations-loading-secondary">
                {recommenderLoadingSecondary}
              </p>
            </div>
          )}
          {!recommendationsLoading && recommendations.length > 0 && (
            <>
              <h3 className="home-combo__recommendations-title">
                {recommenderTitle}
              </h3>
              <ul className="home-combo__recommendations-list">
                {recommendations.map((rec) => (
                  <li key={rec} className="home-combo__recommendations-item">
                    <Link
                      className="home-combo__recommendations-link"
                      to={`${selectStylePath}?vehicleType=${vehicleType}&combination=${rec}`}
                    >
                      {rec}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
};

HeroComboChecker.propTypes = {
  onCheckCombo: PropTypes.func.isRequired,
  selectStylePath: PropTypes.string,
  maxChars: PropTypes.shape({
    car: PropTypes.number,
    motorcycle: PropTypes.number,
  }),
  placeholder: PropTypes.shape({
    car: PropTypes.string,
    motorcycle: PropTypes.string,
  }),
  disclaimerText: PropTypes.string,
  checkAvailButton: PropTypes.string,
  selectStyleLabel: PropTypes.string,
  tryAnotherComboText: PropTypes.string,
  resetLabel: PropTypes.string,
  recommenderTitle: PropTypes.string,
  recommenderLoadingPrimary: PropTypes.string,
  recommenderLoadingSecondary: PropTypes.string,
  recommendations: PropTypes.arrayOf(PropTypes.string),
  recommendationsLoading: PropTypes.bool,
  serverError: PropTypes.string,
};

export default HeroComboChecker;
